import React, { createContext, useState, useEffect, ReactNode, useContext } from 'react';
import { deletePost, getAllPosts, getClientsPosts, updatePostContent, updatePostContacts } from '../services/postService';
import { Post } from '../types/Post';
import { useAuth } from './AuthContext';

interface PostContextType {
  posts: Post[];
  filteredPosts: Post[];
  selectedPost: Post | null;
  loading: boolean;
  error: string | null;
  fetchPosts: () => void;
  refreshPosts: () => Promise<void>;
  handleSetSelectedPost: (Post: Post | null) => void;
  searchPosts: (searchTerm: string) => void;
  deletePostById: (postId: number) => Promise<void>;
  updatePostContent: (postId: number, content: string) => Promise<void>; 
  updatePostContacts: (postId: number, contactName: string, contactType: string, contactPhone: string) => Promise<void>; 
}

const PostContext = createContext<PostContextType | undefined>(undefined);

export const PostProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isBroker } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPost, setSelectedPost] = useState<Post | null>(
    localStorage.getItem('selectedPost') ? JSON.parse(localStorage.getItem('selectedPost') as string) : null
  );

  const fetchPosts = async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      if (isBroker) {
        data = await getAllPosts();
      } else {
        data = await getClientsPosts();
      }
      setPosts(data);
      setFilteredPosts(data);
    } catch (err) {
      setError('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isBroker !== undefined) {
      fetchPosts(); 
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBroker]); 

  const refreshPosts = async () => {
    await fetchPosts();
    localStorage.removeItem('PostData');
  };

  const handleSetSelectedPost = (Post: Post | null) => {
    setSelectedPost(Post);
    localStorage.setItem('selectedPost', JSON.stringify(Post));
  };

  const searchPosts = (searchTerm: string) => {
    const lowerCasedTerm = searchTerm.toLowerCase();
    const filtered = posts.filter(post =>
      post.postText.toLowerCase().includes(lowerCasedTerm) || 
      post.realtorFullName?.toLowerCase().includes(lowerCasedTerm) ||
      post.userFullName?.toLowerCase().includes(lowerCasedTerm)
    );
    setFilteredPosts(filtered);
  };

  const deletePostById = async (postId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await deletePost(postId);
      if (response?.status === 200) {
        await refreshPosts();
      } else {
        setError('No se pudo eliminar el post');
      }
    } catch (err) {
      setError('Error al eliminar el post');
    } finally {
      setLoading(false);
    }
  };


  const handleUpdatePostContent = async (postId: number, content: string) => {
    setLoading(true);
    setError(null);
    try {
      await updatePostContent(postId, content);
      await refreshPosts(); 
    } catch (err) {
      setError('Error al actualizar el contenido del post');
    } finally {
      setLoading(false);
    }
  };


  const handleUpdatePostContacts = async (
    postId: number,
    contactName: string,
    contactType: string,
    contactPhone: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      await updatePostContacts(postId, contactName, contactType, contactPhone);
      await refreshPosts(); 
    } catch (err) {
      setError('Error al actualizar los contactos del post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PostContext.Provider
      value={{
        posts,
        filteredPosts,
        selectedPost,
        deletePostById,
        loading,
        error,
        fetchPosts,
        refreshPosts,
        handleSetSelectedPost,
        searchPosts,
        updatePostContent: handleUpdatePostContent, 
        updatePostContacts: handleUpdatePostContacts, 
      }}
    >
      {children}
    </PostContext.Provider>
  );
};

export const usePost = () => {
  const context = useContext(PostContext);
  if (!context) {
    throw new Error('usePost must be used within a PostProvider');
  }
  return context;
};